import React from "react";
import { useGet } from "restful-react";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import intl from "react-intl-universal";

import { getMatchWithballlData, IMatchWithballDataResponse } from "apis";
import { summariseWithBallTeamReport, getActionSummaryFromReport } from "utils";
import { DataCompareBar } from "components/DataCompareBar";
import { EVENT_VALUES } from "modules/withball-data-input/constants";

interface IWithballDataCompareProps {
  matchId: string;
  homeTeamId?: string;
  awayTeamId?: string;
}

export const WithballDataCompare = ({
  matchId,
  homeTeamId,
  awayTeamId,
}: IWithballDataCompareProps) => {
  const { data } = useGet<IMatchWithballDataResponse>({
    path: getMatchWithballlData(matchId),
  });

  if (!data) {
    return null;
  }

  const homeTeamDataArray = homeTeamId
    ? data.withballData.filter((each) => each.teamid.toString() === homeTeamId)
    : [];
  const awayTeamDataArray = awayTeamId
    ? data.withballData.filter((each) => each.teamid.toString() === awayTeamId)
    : [];
  const homeTeamSummary = summariseWithBallTeamReport(homeTeamDataArray);
  const awayTeamSummary = summariseWithBallTeamReport(awayTeamDataArray);

  const homePassSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.PASS
  );
  const awayPassSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.PASS
  );

  const homeDribbleSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.DRIBBLE
  );
  const awayDribbleSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.DRIBBLE
  );

  const homeShootSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.SHOOT
  );
  const awayShootSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.SHOOT
  );

  const homeTackleSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.TACKLE
  );
  const awayTackleSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.TACKLE
  );

  const homeInterceptionSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.INTERCEPTION
  );
  const awayInterceptionSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.INTERCEPTION
  );

  const homeClearSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.CLEARANCE
  );
  const awayClearSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.CLEARANCE
  );

  const homeBlockSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.BLOCK
  );
  const awayBlockSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.BLOCK
  );

  const homeSavingSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.SAVING
  );
  const awaySavingSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.SAVING
  );

  const homeFreeKickSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.FREE_KICK
  );
  const awayFreeKickSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.FREE_KICK
  );

  const homeCornerKickSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.CORNER_KICK
  );
  const awayCornerKickSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.CORNER_KICK
  );

  const homePenaltyKickSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.PENALTY
  );
  const awayPenaltyKickSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.PENALTY
  );

  const homeFoulSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.FOUL
  );
  const awayFoulSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.FOUL
  );

  const homeYellowSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.YELLOW
  );
  const awayYellowSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.YELLOW
  );

  const homeRedSummary = getActionSummaryFromReport(
    homeTeamSummary,
    EVENT_VALUES.RED
  );
  const awayRedSummary = getActionSummaryFromReport(
    awayTeamSummary,
    EVENT_VALUES.RED
  );

  return (
    <Paper>
      <Box pt={4} pb={4}>
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={`${homePassSummary.success}/${homePassSummary.total}`}
              leftValue={homePassSummary.success}
              rightLabel={`${awayPassSummary.success}/${awayPassSummary.total}`}
              rightValue={awayPassSummary.success}
              label={intl.get("STATS_LABEL_PASS")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={`${homeDribbleSummary.success}/${homeDribbleSummary.total}`}
              leftValue={homeDribbleSummary.success}
              rightLabel={`${awayDribbleSummary.success}/${awayDribbleSummary.total}`}
              rightValue={awayDribbleSummary.success}
              label={intl.get("STATS_LABEL_DRIBBLE")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={`${homeShootSummary.success}/${homeShootSummary.total}`}
              leftValue={homeShootSummary.success}
              rightLabel={`${awayShootSummary.success}/${awayShootSummary.total}`}
              rightValue={awayShootSummary.success}
              label={intl.get("STATS_LABEL_SHOOT")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeTackleSummary.total.toString()}
              leftValue={homeTackleSummary.total}
              rightLabel={awayTackleSummary.total.toString()}
              rightValue={awayTackleSummary.total}
              label={intl.get("STATS_LABEL_TACKLE")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeInterceptionSummary.total.toString()}
              leftValue={homeInterceptionSummary.total}
              rightLabel={awayInterceptionSummary.total.toString()}
              rightValue={awayInterceptionSummary.total}
              label={intl.get("STATS_LABEL_INTERCEPTION")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeClearSummary.total.toString()}
              leftValue={homeClearSummary.total}
              rightLabel={awayClearSummary.total.toString()}
              rightValue={awayClearSummary.total}
              label={intl.get("STATS_LABEL_CLEAR")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeBlockSummary.total.toString()}
              leftValue={homeBlockSummary.total}
              rightLabel={awayBlockSummary.total.toString()}
              rightValue={awayBlockSummary.total}
              label={intl.get("STATS_LABEL_BLOCK")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeSavingSummary.total.toString()}
              leftValue={homeSavingSummary.total}
              rightLabel={awaySavingSummary.total.toString()}
              rightValue={awaySavingSummary.total}
              label={intl.get("STATS_LABEL_SAVE")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeFreeKickSummary.total.toString()}
              leftValue={homeFreeKickSummary.total}
              rightLabel={awayFreeKickSummary.total.toString()}
              rightValue={awayFreeKickSummary.total}
              label={intl.get("STATS_LABEL_FREEKICK")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeCornerKickSummary.total.toString()}
              leftValue={homeCornerKickSummary.total}
              rightLabel={awayCornerKickSummary.total.toString()}
              rightValue={awayCornerKickSummary.total}
              label={intl.get("STATS_LABEL_CORNER")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homePenaltyKickSummary.total.toString()}
              leftValue={homePenaltyKickSummary.total}
              rightLabel={awayPenaltyKickSummary.total.toString()}
              rightValue={awayPenaltyKickSummary.total}
              label={intl.get("STATS_LABEL_PENALTY")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeFoulSummary.total.toString()}
              leftValue={homeFoulSummary.total}
              rightLabel={awayFoulSummary.total.toString()}
              rightValue={awayFoulSummary.total}
              label={intl.get("STATS_LABEL_FOUL")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeYellowSummary.total.toString()}
              leftValue={homeYellowSummary.total}
              rightLabel={awayYellowSummary.total.toString()}
              rightValue={awayYellowSummary.total}
              label={intl.get("STATS_LABEL_YELLOW")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <Box mt={2} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <DataCompareBar
              leftLabel={homeRedSummary.total.toString()}
              leftValue={homeRedSummary.total}
              rightLabel={awayRedSummary.total.toString()}
              rightValue={awayRedSummary.total}
              label={intl.get("STATS_LABEL_RED")}
            />
          </Grid>
          <Grid item xs={1} />
        </Grid>
      </Box>
    </Paper>
  );
};
